import React, { useEffect, useState, useMemo, useRef } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
// icons
import TaskIcon from '@mui/icons-material/Task';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FlagIcon from '@mui/icons-material/Flag';
import SensorsIcon from '@mui/icons-material/Sensors';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import SpeedIcon from '@mui/icons-material/Speed';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InsightsIcon from '@mui/icons-material/Insights';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AirIcon from '@mui/icons-material/Air';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
//hooks
import { useOrgs, useSites } from '../../hooks/useOrgSiteData';
import { useSelections } from '../../context/SelectionsProvider';
import { useLocation } from 'react-router-dom';

const AdminCanvasDrawerOptions = props => {
    const {pathname} = useLocation();

    const { selectedOrgID, setSelectedOrgID, selectedSiteID, setSelectedSiteID, selectedTabName, setSelectedTabName } = useSelections();

    const {
        data: orgs = [],
        isLoading: orgsLoading,
        isError: orgsError,
        error: orgsErr,
    } = useOrgs();

    const sitesParams = selectedOrgID ? { orgID: selectedOrgID } : {};

    const {
        data: sites = [],
        isLoading: sitesLoading,
        isError: sitesError,
        error: sitesErr
    } = useSites(sitesParams);

    const sortedOrgs = useMemo(
        () => [...orgs].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
        [orgs]
    );

    const sortedSites = useMemo(
        () => [...sites].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
        [sites]
    );

    useEffect(() => {
        if (orgsLoading) return;
        const haveOrgs = sortedOrgs.length > 0;
        const orgValid = selectedOrgID && sortedOrgs.some(o => String(o.id) === String(selectedOrgID));
        if (haveOrgs && !orgValid) {
            if(window.location.pathname.startsWith('/client')){
                let orgPathID = window.location.pathname.split('/').slice(-1)[0];

                let specifiedOrg = sortedOrgs.filter(org => org.id === orgPathID);

                if(specifiedOrg.length === 1) setSelectedOrgID(specifiedOrg[0].id);
                else setSelectedOrgID(String(sortedOrgs[0].id));

            }else if(window.location.pathname.startsWith('/sites')){
                let sitePathID = parseInt(window.location.pathname.split('/').slice(-1)[0]);

                let specifiedOrg = sortedOrgs.filter(org => org.siteIDs.includes(sitePathID));

                if(specifiedOrg.length === 1) setSelectedOrgID(specifiedOrg[0].id);
                else setSelectedOrgID(String(sortedOrgs[0].id));

            }else{
                setSelectedOrgID(String(sortedOrgs[0].id));
            }
        }
    }, [orgsLoading, sortedOrgs, selectedOrgID, setSelectedOrgID]);

    useEffect(() => {
        if (!selectedOrgID || sitesLoading) return;

        const siteIds = new Set(sortedSites.map(s => Number(s.id)));
        const hasValidSel = selectedSiteID && siteIds.has(Number(selectedSiteID));

        if (!hasValidSel) {
            setSelectedSiteID(sortedSites[0]?.id ?? '');
        }
    }, [selectedOrgID, sitesLoading, sortedSites, selectedSiteID, setSelectedSiteID]);

    const updateTabNameAndClose = newTabName => {
        setSelectedTabName(newTabName);
        props.handleClose();
    }

    return (
        <>
            <div>
                <FormControl style={{width:'100%', marginBottom:'1.25vh'}}>
                    <InputLabel>Organization</InputLabel>
                    <Select
                        value={selectedOrgID}
                        label="Organization"
                        onChange={e => {
                            setSelectedOrgID(e.target.value);
                            setSelectedTabName('');
                        }}
                        disabled={orgsLoading}
                    >
                        {
                            sortedOrgs.map(org => (
                                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                            ))
                        }
                    </Select>
                    {orgsError && (
                        <small style={{ color: 'crimson' }}>
                            Failed to load orgs: {String(orgsErr?.message || '')}
                        </small>
                    )}
                </FormControl>

                <FormControl style={{width:'100%', marginBottom:'1.25vh'}}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={selectedSiteID ?? ''}
                        label="Site"
                        onChange={e => {
                            setSelectedTabName('');
                            setSelectedSiteID(Number(e.target.value));
                        }}
                        disabled={sitesLoading || !selectedOrgID}
                    >
                        {sortedSites.map(site => (
                            <MenuItem key={site.id} value={Number(site.id)}>{site.name}</MenuItem>
                        ))}
                    </Select>
                    {sitesError && (
                        <small className="text-error">
                            Failed to load sites: {String(sitesErr?.message || '')}
                        </small>
                    )}
                </FormControl>
            </div>

            {(selectedOrgID === '' || selectedSiteID === '') && (
                <p style={{textAlign:'center', margin:'0', fontSize:'small'}}>
                    Select an org and site to access options
                </p>
            )}  

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID} onClick={() => updateTabNameAndClose('opportunities')}>
                    <ListItemIcon>
                        <LightbulbIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Opportunities" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('metrics')}>
                    <ListItemIcon>
                        <EnergySavingsLeafIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Raw Data" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('impacts')}>
                    <ListItemIcon>
                        <SpeedIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Savings" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('glidepath')}>
                    <ListItemIcon>
                        <InsightsIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Glidepath" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>
            <Divider sx={{ borderColor: 'black', my: 1 }} textAlign="left">
                <span style={{ color: 'black', fontWeight: 'bold' }}>Modules</span>
            </Divider>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('aircompressor')}>
                    <ListItemIcon>
                        <AirIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Air Compressors" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('boiler')}>
                    <ListItemIcon>
                        <LocalFireDepartmentIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Boilers" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <Divider sx={{ borderColor: 'black', my: 1 }} textAlign="left">
                <span style={{ color: 'black', fontWeight: 'bold' }}>Logs</span>
            </Divider>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('tasks')}>
                    <ListItemIcon>
                        <TaskIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Tasks" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('activity')}>
                    <ListItemIcon>
                        <AssignmentIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Activity Log" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <Divider sx={{ borderColor: 'black', my: 1 }} textAlign="left">
                <span style={{ color: 'black', fontWeight: 'bold' }}>Inputs</span>
            </Divider>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('utilities')}>
                    <ListItemIcon>
                        <CreateNewFolderIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Utility Bills" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('targets')}>
                    <ListItemIcon>
                        <FlagIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Targets" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('sensors')}>
                    <ListItemIcon>
                        <SensorsIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Sensors" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID}  onClick={() => updateTabNameAndClose('properties')}>
                    <ListItemIcon>
                        <LocationCityIcon className="text-brand"/>
                    </ListItemIcon>
                    <ListItemText primary="Site Properties" className="text-muted-dark"/>
                </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
                <ListItemButton disabled={selectedOrgID === '' || !selectedSiteID} onClick={() => updateTabNameAndClose('admin-tables')}>
                    <ListItemIcon>
                        <AdminPanelSettingsIcon style={{color:'rgb(8,114,182)'}}/>
                    </ListItemIcon>
                    <ListItemText primary="Admin Tables" style={{color:'rgb(78,79,83)'}}/>
                </ListItemButton>
            </ListItem>
        </>
    );
}

export default AdminCanvasDrawerOptions;
